import { useEffect, useState } from "react";
import {
  Badge,
  Body1,
  Button,
  Card,
  CardHeader,
  Caption1,
  Divider,
  Link,
  Spinner,
  Subtitle2,
  makeStyles,
  tokens
} from "@fluentui/react-components";
import { config } from "../config";
import { useAuthHelper } from "../contexts/AuthHelper";
import { useSubscription } from "../contexts/Subscription";
import { ExistingSubscription } from "../model/model";
import { ContactPublisherCallout } from "./common/ContactPublisherCallout";
import { EmptyState } from "./common/EmptyState";
import { StatusMessage } from "./common/StatusMessage";
import { UsersTable } from "./users/UsersTable";

const useStyles = makeStyles({
  root: {
    display: "grid",
    gap: tokens.spacingVerticalXL,
  },
  details: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: tokens.spacingVerticalM,
  },
  detailItem: {
    display: "grid",
    gap: tokens.spacingVerticalXS,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  actions: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
    alignItems: "center",
  },
});

export const Manage: React.FC = () => {
  const subscription = useSubscription();
  const auth = useAuthHelper();
  const styles = useStyles();

  const tenantId: string | undefined = auth.getUserTenant();

  const [loading, setLoading] = useState<boolean>(false);
  const [subscr, setSubscr] = useState<ExistingSubscription | undefined>(undefined);
  const [err, setErr] = useState<string | undefined>(undefined);
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);
  const [deletingUserId, setDeletingUserId] = useState<string | undefined>(undefined);
  const [refresh, callRefresh] = useState<number>(0);

  useEffect(() => {
    if (tenantId) {
      load();
    }
  }, [tenantId, refresh]);

  const load = async () => {
    setLoading(true);
    setErr(undefined);

    try {
      const nextSubscription = await subscription.getExistingSubscription(
        tenantId ?? "",
        await auth.getAccessToken() ?? ""
      );
      setSubscr(nextSubscription);
    }
    catch (error) {
      setSubscr(undefined);
      setErr(error instanceof Error ? error.message : String(error));
    }
    finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId: string, userTenantId: string) => {
    if (!subscr) {
      return;
    }

    setDeleteError(undefined);
    setDeletingUserId(userId);

    try {
      await subscription.removeUser(
        subscr.license.subscriptionId,
        userTenantId,
        userId,
        await auth.getAccessToken() ?? ""
      );
      callRefresh((current) => current + 1);
    }
    catch (error) {
      setDeleteError(error instanceof Error ? error.message : String(error));
    }
    finally {
      setDeletingUserId(undefined);
    }
  };

  const adminConsentUrl = `https://login.microsoftonline.com/organizations/v2.0/adminconsent?client_id=${config.ADD_IN_CLIENT_ID}&scope=${config.ADD_IN_SCOPE}&redirect_uri=${config.ADD_IN_CONSENT_REDIRECT_URL}`;

  return (
    <div className={styles.root}>
      <Card>
        <CardHeader
          header={<Subtitle2>Subscription management</Subtitle2>}
          description={
            <Caption1>
              Review seats, Outlook add-in permissions, and tenant users from a single administration workspace.
            </Caption1>
          }
        />
      </Card>

      {loading && (
        <Card>
          <Spinner label="Loading subscription data" />
        </Card>
      )}

      {!tenantId && !loading && (
        <EmptyState
          title="Tenant context is unavailable"
          description="Sign in with a Microsoft work account to load the subscription assigned to your tenant."
        />
      )}

      {err && (
        <>
          <StatusMessage intent="error" title="Subscription load failed">
            {err}
          </StatusMessage>
          <ContactPublisherCallout />
        </>
      )}

      {deleteError && (
        <>
          <StatusMessage intent="error" title="User removal failed">
            {deleteError}
          </StatusMessage>
          <ContactPublisherCallout />
        </>
      )}

      {tenantId && !loading && !subscr && !err && (
        <EmptyState
          title="No subscription found"
          description="No subscription data is available for the signed-in tenant yet."
        />
      )}

      {tenantId && !loading && subscr && (
        <>
          <Card>
            <div className={styles.root}>
              <CardHeader
                header={<Subtitle2>Subscription summary</Subtitle2>}
                description={<Caption1>Core licensing information for the current tenant.</Caption1>}
              />
              <div className={styles.actions}>
                <Badge appearance="filled" color="success">
                  {subscr.license.subscription_status}
                </Badge>
                <Caption1>
                  {subscr.users.length} of {subscr.license.usersQuantityAllowed} assigned seats in use
                </Caption1>
              </div>
              <div className={styles.details}>
                <div className={styles.detailItem}>
                  <Caption1>Subscription ID</Caption1>
                  <Body1>{subscr.license.subscriptionId}</Body1>
                </div>
                <div className={styles.detailItem}>
                  <Caption1>Plan</Caption1>
                  <Body1>{subscr.license.planRefRK}</Body1>
                </div>
                <div className={styles.detailItem}>
                  <Caption1>Seats allowed</Caption1>
                  <Body1>{subscr.license.usersQuantityAllowed}</Body1>
                </div>
                <div className={styles.detailItem}>
                  <Caption1>Plan maximum</Caption1>
                  <Body1>{subscr.plan.maxUsers}</Body1>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <div className={styles.root}>
              <CardHeader
                header={<Subtitle2>Outlook add-in admin consent</Subtitle2>}
                description={
                  <Caption1>
                    Grant admin consent if the add-in still needs tenant-wide permissions.
                  </Caption1>
                }
              />
              <Body1>
                Use this action only when the add-in has not yet received the required Microsoft 365 admin consent.
              </Body1>
              <Divider />
              <div className={styles.actions}>
                <Link href={adminConsentUrl}>Grant admin consent</Link>
                <Caption1>Requires a Microsoft 365 administrator account.</Caption1>
              </div>
            </div>
          </Card>

          <Card>
            <div className={styles.root}>
              <CardHeader
                header={<Subtitle2>Tenant users</Subtitle2>}
                description={<Caption1>View who currently occupies a seat and remove users when needed.</Caption1>}
              />
              <UsersTable
                users={subscr.users}
                deletingUserId={deletingUserId}
                onDelete={deleteUser}
              />
              <div className={styles.actions}>
                <Button appearance="secondary" onClick={() => callRefresh((current) => current + 1)}>
                  Refresh data
                </Button>
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};
